/**
 * PDF text extraction utility.
 *
 * Uses `unpdf` to extract text content from PDF files and buffers.
 *
 * Extraction is **layout-aware**: text items are grouped into visual rows by
 * their PDF coordinates (Y for rows, X within a row) so that box-based forms
 * (CMS-1500, EOBs, superbills) reconstruct in reading order instead of the
 * flat, coordinate-free blob `extractText({ mergePages: true })` produces. This
 * lets a consumer reliably map text to its position on the page (e.g. the
 * physician signature in Box 31 vs. the patient signature in Box 12). When a PDF
 * has no usable text layer, we fall back to the flat merge so scanned/text-less
 * PDFs never regress. When present, filled AcroForm field values are appended as
 * a labeled `Form fields:` section.
 *
 * Includes size guards to prevent excessive memory usage.
 *
 * @module pdf-extract
 */

import * as fs from 'fs/promises';
import type { StructuredTextItem } from 'unpdf';
import { consoleLogger, type BrowserLogger } from './logger.js';

let _logger: BrowserLogger = consoleLogger;

export function setPdfExtractLogger(l: BrowserLogger): void {
  _logger = l;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
const MAX_OUTPUT_CHARS = 100_000;

/**
 * Extract text content from a PDF buffer.
 * Returns the extracted text and total page count.
 */
export async function extractPdfTextFromBuffer(
  buffer: Buffer
): Promise<{ text: string; totalPages: number }> {
  if (buffer.length > MAX_FILE_SIZE_BYTES) {
    _logger.warn('[PdfExtract] PDF too large for text extraction', {
      sizeBytes: buffer.length,
      maxBytes: MAX_FILE_SIZE_BYTES,
    });
    return {
      text: `[PDF too large for text extraction: ${formatBytes(buffer.length)} exceeds ${formatBytes(MAX_FILE_SIZE_BYTES)} limit]`,
      totalPages: 0,
    };
  }

  const unpdf = await import('unpdf');

  // Primary path: layout-aware reconstruction from positioned text items.
  let text = '';
  let totalPages = 0;
  let invalidPdf = false;
  let usedFallback = false;

  try {
    const layout = await extractLayoutText(buffer, unpdf);
    text = layout.text;
    totalPages = layout.totalPages;
  } catch (err) {
    if (isInvalidPdfError(err)) {
      _logger.warn('[PdfExtract] Invalid PDF structure', {
        sizeBytes: buffer.length,
        error: err instanceof Error ? err.message : String(err),
      });
      return {
        text: '[PDF text extraction failed: the file is not a valid PDF or its structure is corrupted]',
        totalPages: 0,
      };
    }
    _logger.warn('[PdfExtract] Layout extraction failed; falling back to flat text', {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  // Fallback: the flat merge path. Used when layout extraction threw a
  // non-fatal error or produced no usable text (e.g. a scanned/image-only PDF
  // whose text layer is empty).
  if (text.trim().length === 0) {
    usedFallback = true;
    const flat = await unpdf
      .extractText(new Uint8Array(buffer), { mergePages: true })
      .catch((err: unknown) => {
        if (isInvalidPdfError(err)) {
          invalidPdf = true;
          _logger.warn('[PdfExtract] Invalid PDF structure', {
            sizeBytes: buffer.length,
            error: err instanceof Error ? err.message : String(err),
          });
          return {
            text: '[PDF text extraction failed: the file is not a valid PDF or its structure is corrupted]',
            totalPages: 0,
          };
        }
        throw err;
      });
    text = flat.text ?? '';
    totalPages = flat.totalPages ?? totalPages;
  }

  if (text.length > MAX_OUTPUT_CHARS) {
    _logger.info('[PdfExtract] Truncating extracted text', {
      originalLength: text.length,
      truncatedTo: MAX_OUTPUT_CHARS,
      totalPages,
    });
    text = text.slice(0, MAX_OUTPUT_CHARS) + `\n\n[Text truncated at ${MAX_OUTPUT_CHARS} characters]`;
  }

  if (!invalidPdf) {
    _logger.info('[PdfExtract] Text extracted successfully', {
      textLength: text.length,
      totalPages,
      sizeBytes: buffer.length,
      mode: usedFallback ? 'flat-fallback' : 'layout',
    });
  }

  return { text, totalPages };
}

/**
 * Extract text content from a PDF file on disk.
 */
export async function extractPdfText(
  filePath: string
): Promise<{ text: string; totalPages: number }> {
  const stat = await fs.stat(filePath);

  if (stat.size > MAX_FILE_SIZE_BYTES) {
    _logger.warn('[PdfExtract] PDF file too large for text extraction', {
      filePath,
      sizeBytes: stat.size,
      maxBytes: MAX_FILE_SIZE_BYTES,
    });
    return {
      text: `[PDF too large for text extraction: ${formatBytes(stat.size)} exceeds ${formatBytes(MAX_FILE_SIZE_BYTES)} limit]`,
      totalPages: 0,
    };
  }

  const buffer = await fs.readFile(filePath);
  return extractPdfTextFromBuffer(Buffer.from(buffer));
}

type UnpdfModule = typeof import('unpdf');

/**
 * Reconstruct layout-aware text from a PDF's positioned text items, then append
 * any filled AcroForm field values.
 */
async function extractLayoutText(
  buffer: Buffer,
  unpdf: UnpdfModule
): Promise<{ text: string; totalPages: number }> {
  // A fresh Uint8Array per call: pdf.js may detach the backing buffer when it
  // parses, so never share one array across unpdf calls.
  const doc = await unpdf.getDocumentProxy(new Uint8Array(buffer));
  const { totalPages, items } = await unpdf.extractTextItems(doc);

  let text = buildLayoutText(items);

  const fields = await extractFormFields(doc);
  if (fields) {
    text = text.length > 0 ? `${text}\n\nForm fields:\n${fields}` : `Form fields:\n${fields}`;
  }

  return { text, totalPages };
}

/**
 * Group positioned text items into visual rows and join them in reading order.
 *
 * - Rows: items sharing (approximately) the same baseline Y, ordered top→bottom.
 * - Columns: items within a row ordered left→right, with the inter-item spacing
 *   widened when there is a visible horizontal gap so distinct form fields on
 *   the same line stay separated.
 *
 * Exported for unit testing the reconstruction independently of PDF parsing.
 */
export function buildLayoutText(pages: StructuredTextItem[][]): string {
  const out: string[] = [];
  const multiPage = pages.length > 1;

  pages.forEach((items, pageIndex) => {
    const kept = items.filter((i) => i.str && i.str.trim().length > 0);
    if (kept.length === 0) return;

    // Top→bottom (Y descending; PDF origin is bottom-left), then left→right.
    kept.sort((a, b) => b.y - a.y || a.x - b.x);

    const rows: StructuredTextItem[][] = [];
    let current: StructuredTextItem[] | null = null;
    let anchorY = 0;
    for (const item of kept) {
      const tolerance = Math.max(item.height || item.fontSize || 8, 4) * 0.6;
      if (current && Math.abs(item.y - anchorY) <= tolerance) {
        current.push(item);
      } else {
        current = [item];
        anchorY = item.y;
        rows.push(current);
      }
    }

    const lines = rows.map((row) => {
      row.sort((a, b) => a.x - b.x);
      let line = '';
      let prev: StructuredTextItem | null = null;
      for (const item of row) {
        if (prev) {
          const gap = item.x - (prev.x + prev.width);
          const charWidth = prev.width / Math.max(prev.str.length, 1) || 4;
          if (gap < charWidth * 0.5) line += '';
          else if (gap < charWidth * 4) line += ' ';
          else line += '    ';
        }
        line += item.str;
        prev = item;
      }
      return line.trimEnd();
    });

    if (multiPage) out.push(`--- Page ${pageIndex + 1} ---`);
    out.push(lines.join('\n'));
  });

  return out.join('\n');
}

/**
 * Extract filled AcroForm field values as `fieldName = value` lines.
 *
 * Best-effort and defensive: some runtimes cannot serialize pdf.js field
 * objects across its worker boundary (e.g. Bun throws `DataCloneError`), so any
 * failure is swallowed — form-field extraction must never break text output.
 */
async function extractFormFields(doc: {
  getFieldObjects: () => Promise<Record<string, unknown[]> | null>;
}): Promise<string> {
  try {
    const fieldObjects = await doc.getFieldObjects();
    if (!fieldObjects) return '';

    const lines: string[] = [];
    for (const [name, entries] of Object.entries(fieldObjects)) {
      if (!Array.isArray(entries)) continue;
      for (const entry of entries) {
        const value = (entry as { value?: unknown } | null)?.value;
        if (value === undefined || value === null) continue;
        const str = String(value).trim();
        if (!str) continue;
        lines.push(`${name} = ${str}`);
        break; // one value per field name
      }
    }
    return lines.join('\n');
  } catch (err) {
    _logger.debug('[PdfExtract] AcroForm field extraction skipped', {
      error: err instanceof Error ? err.message : String(err),
    });
    return '';
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function isInvalidPdfError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.name === 'InvalidPDFException' || err.message.includes('Invalid PDF structure');
}
